import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Paper,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useStatisticJob from '../../hooks/useStatisticJob';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import StatisticViewShell from '../ui/StatisticViewShell';
import StatisticMatrixTables from '../ui/StatisticMatrixTables';
import { percentFormatter } from '../../util/formatHelper';
import { isFeatureEnabled } from '../../config/featureFlags';
import StaffCostEstimateNotice from '../ui/StaffCostEstimateNotice';
import { STATISTIC_TYPE_CALL_CENTER, STATISTIC_TYPE_AREA_STAFF_COST } from '../../constants/statisticTypes';
import {
  getBarTooltipCursor,
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsTooltipProps,
  getStatisticsPlainAccordionSx,
  getStatisticsSurfaceBackground,
  getStatisticsTableRowBorderTop,
  getStatisticsKpiLabelColor,
  getStatisticsKpiHelperColor,
  getRechartsLegendBarProps,
  getRechartsLegendPieProps,
  getStatisticsHtmlThStyle,
  getStatisticsPageRootStyle,
} from '../../util/chartTheme';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const defaultStartDate = dayjs().startOf('month').format('YYYY-MM-DD');
const defaultEndDate = dayjs().endOf('month').format('YYYY-MM-DD');

const CALL_VOLUME_METRICS = [
  { key: 'callsReceived', label: 'Eingegangene Anrufe' },
  { key: 'callsReceivedUnique', label: 'Eingegangene Anrufe (unique)' },
  { key: 'callbacksOpen', label: 'Offene Rückrufe (Tagesende)' },
  { key: 'callbackCount', label: 'Durchgeführte Rückrufe' },
  { key: 'callsDirectlyAnswered', label: 'Direkt beantwortete Anrufe' },
  { key: 'callsDirectlyAnsweredQuote', label: 'Anteil direkt beantwortete Anrufe', unit: '%', isAverage: true },
  { key: 'callsDurationAverage', label: 'Durchschnittliche Anrufdauer', unit: 'sec', isAverage: true },
  { key: 'callsWaittimeAverage', label: 'Durchschnittliche Wartedauer', unit: 'sec', isAverage: true },
];

const CallCenterStatistics = () => {
  const theme = useTheme();
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const axisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const chartLegendBarProps = useMemo(() => getRechartsLegendBarProps(theme), [theme]);
  const chartLegendPieProps = useMemo(() => getRechartsLegendPieProps(theme), [theme]);
  const htmlThStyle = useCallback((extra = {}) => getStatisticsHtmlThStyle(theme, extra), [theme]);
  const chartTooltipProps = useMemo(() => getRechartsTooltipProps(theme), [theme]);
  const plainAccordionSx = useMemo(() => getStatisticsPlainAccordionSx(theme), [theme]);
  const barTooltipCursor = useMemo(() => getBarTooltipCursor(theme), [theme]);
  const tableRowBorderTop = useMemo(() => getStatisticsTableRowBorderTop(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);
  const kpiHelperColor = useMemo(() => getStatisticsKpiHelperColor(theme), [theme]);
  const chartPaletteColors = useMemo(
    () => [
      theme.palette.success.main,
      theme.palette.primary.main,
      theme.palette.warning.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.grey[700],
      theme.palette.grey[600],
      theme.palette.secondary.dark,
      theme.palette.primary.dark,
    ],
    [theme]
  );
  const activityLineColors = useMemo(
    () => ({
      changes: theme.palette.warning.main,
      bookings: theme.palette.success.main,
      hourly: theme.palette.primary.main,
    }),
    [theme]
  );
  const [startDate, setStartDate] = useState(dayjs(defaultStartDate));
  const [endDate, setEndDate] = useState(dayjs(defaultEndDate));
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  });
  const [chartType, setChartType] = useState('line');
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  const filterParams = useMemo(
    () => ({
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      branchScope: 'all',
    }),
    [appliedFilter]
  );

  const {
    payload: stats,
    isStale,
    cacheComputedAt,
    cacheExpiresAt,
    status,
    job,
    error,
    loadCacheEntry,
    refresh: refreshStatistic,
  } = useStatisticJob({
    statisticType: STATISTIC_TYPE_CALL_CENTER,
    endpoint: '/api/v1/statistic/callCenter',
    filterParams,
  });

  const { entries: cacheEntries, isLoading: entriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_CALL_CENTER,
  });

  // Personnel cost of the Call-Center team (role-based area cost), shown as an
  // extra section alongside the call-center activity data.
  const authCtx = useContext(AuthContext);
  const canSeeStaffCost =
    authCtx.checkRight('statistic:special:staffCost');
  const ccStaffCostFilterParams = useMemo(
    () =>
      canSeeStaffCost
        ? {
            startDate: appliedFilter.startDate,
            endDate: appliedFilter.endDate,
            area: 'Callcenter',
            branches: '',
            employerCostFactor: '1.3',
            branchScope: 'all',
          }
        : null,
    [appliedFilter, canSeeStaffCost]
  );
  const ccStaffCost = useStatisticJob({
    statisticType: STATISTIC_TYPE_AREA_STAFF_COST,
    endpoint: '/api/v1/statistic/areaStaffCost',
    filterParams: ccStaffCostFilterParams,
  });
  const ccStaffTotals = ccStaffCost.payload?.totals || null;

  useEffect(() => {
    if (status === 'failed' && error) toast.error(error);
  }, [status, error]);

  const handleSelectEntry = useCallback(
    entryId => {
      setSelectedEntryId(entryId);
      if (entryId) loadCacheEntry(entryId);
    },
    [loadCacheEntry]
  );

  const isFetching = status === 'fetching' || status === 'pending' || status === 'running';

  const applyFilters = useCallback(
    (nextStart, nextEnd) => {
      const s = nextStart || startDate;
      const e = nextEnd || endDate;
      if (!s || !e || !s.isValid() || !e.isValid()) {
        toast.error('Bitte wähle gültige Daten aus');
        return;
      }
      if (s.isAfter(e)) {
        toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
        return;
      }
      setSelectedEntryId(null);
      setAppliedFilter({
        startDate: s.format('YYYY-MM-DD'),
        endDate: e.format('YYYY-MM-DD'),
      });
    },
    [startDate, endDate]
  );

  const cards = useMemo(() => {
    if (!stats?.counts) return [];
    const { totalBookings, ccChangedBookings, totalCCChanges, ccUsersActive } = stats.counts;
    const { ccChangePercent } = stats.percentages || {};

    return [
      {
        label: 'Buchungen im Zeitraum',
        value: totalBookings,
        helper: 'Gesamtanzahl der Buchungen',
      },
      {
        label: 'Vom CC bearbeitete Buchungen',
        value: ccChangedBookings,
        helper: `${percentFormatter.format(ccChangePercent || 0)} % aller Buchungen`,
      },
      {
        label: 'Änderungen durch CC',
        value: totalCCChanges,
        helper: 'Gesamtanzahl aller Änderungen',
      },
      {
        label: 'Aktive CC-Mitarbeiter',
        value: ccUsersActive,
        helper: 'Mitarbeiter mit Änderungen',
      },
    ];
  }, [stats]);

  const chartData = useMemo(() => {
    const daily = stats?.timeSeries?.daily || [];
    return daily.map(p => {
      const date = dayjs(p.date);
      return {
        ...p,
        label: date.isValid() ? date.format('DD.MM.') : String(p.date || ''),
      };
    });
  }, [stats]);

  const changeTypePieData = useMemo(() => {
    const types = stats?.changeTypes || [];
    return types
      .slice(0, 10)
      .map(t => ({ name: t.label, value: t.count }))
      .filter(t => t.value > 0);
  }, [stats]);

  const userPieData = useMemo(() => {
    const users = stats?.userStats || [];
    return users
      .slice(0, 8)
      .map(u => ({ name: u.displayName, value: u.changeCount }))
      .filter(u => u.value > 0);
  }, [stats]);

  const hourlyData = useMemo(() => {
    return stats?.hourlyDistribution || [];
  }, [stats]);

  const phoneSummary = stats?.phoneSummary;
  const phoneByDay = useMemo(
    () => (Array.isArray(stats?.phoneByDay) ? stats.phoneByDay : []),
    [stats]
  );

  const fmtInt = n => Number(n || 0).toLocaleString('de-DE');
  const fmtPct = n => `${percentFormatter.format(Number(n || 0))} %`;
  const fmtSec = n => `${fmtInt(n)} sec`;
  const fmtEuro = n =>
    Number(n || 0).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2,
    });

  const ccAdded = stats?.ccAdded || null;
  const ccAddedByDay = useMemo(
    () => (Array.isArray(stats?.ccAddedByDay) ? stats.ccAddedByDay : []),
    [stats]
  );
  const ccAddedByUser = useMemo(
    () => (Array.isArray(stats?.ccAddedByUser) ? stats.ccAddedByUser : []),
    [stats]
  );
  const ccAddedByBranch = useMemo(
    () => (Array.isArray(stats?.ccAddedByBranch) ? stats.ccAddedByBranch : []),
    [stats]
  );
  const ccAddedByWeekday = useMemo(
    () => (Array.isArray(stats?.ccAddedByWeekday) ? stats.ccAddedByWeekday : []),
    [stats]
  );
  const ccAddedByHour = useMemo(
    () => (Array.isArray(stats?.ccAddedByHour) ? stats.ccAddedByHour : []),
    [stats]
  );
  const ccAddedChartData = useMemo(
    () =>
      ccAddedByDay.map(p => ({
        ...p,
        label: dayjs(p.date).isValid() ? dayjs(p.date).format('DD.MM.') : String(p.date || ''),
      })),
    [ccAddedByDay]
  );

  // KI-Telefonie block, delivered by the same endpoint so AI and human calls cannot drift apart.
  // Hidden behind the voiceAi flag; the backend also omits the block while its flag is off.
  const voiceAiEnabled = isFeatureEnabled('voiceAi');
  const ai = useMemo(() => (voiceAiEnabled ? stats?.ai || null : null), [stats, voiceAiEnabled]);
  const aiByDay = useMemo(() => (Array.isArray(ai?.byDay) ? ai.byDay : []), [ai]);
  const aiTopics = useMemo(() => (Array.isArray(ai?.topics) ? ai.topics : []), [ai]);
  const aiOutcomePieData = useMemo(
    () => (Array.isArray(ai?.outcomes) ? ai.outcomes.map(o => ({ name: o.label, value: o.count })) : []),
    [ai]
  );
  // Split of the total call volume: handled by the AI alone, handed over, or straight to a human.
  const aiSplitData = useMemo(() => {
    if (!ai) return [];
    return [
      { name: 'Nur KI', value: ai.counts.aiOnly },
      { name: 'KI → Mensch', value: ai.counts.humanAfterTransfer },
      { name: 'KI, dann Warteschlange', value: ai.counts.humanAfterAiPreceded },
      { name: 'Direkt Mensch', value: ai.counts.humanDirect },
    ].filter(row => row.value > 0);
  }, [ai]);

  // Stimmung: three buckets plus the correlation with the call outcome. `sentiment` is only
  // present from cache version v11 on, so every read is guarded.
  const aiSentiment = useMemo(() => ai?.sentiment || null, [ai]);
  const aiSentimentBuckets = useMemo(
    () => (Array.isArray(aiSentiment?.buckets) ? aiSentiment.buckets.filter(b => b.count > 0) : []),
    [aiSentiment]
  );
  const aiSentimentByOutcome = useMemo(
    () => (Array.isArray(aiSentiment?.byOutcome) ? aiSentiment.byOutcome : []),
    [aiSentiment]
  );
  const aiSentimentTrend = useMemo(
    () => aiByDay.filter(day => day.sentimentScore !== null && day.sentimentScore !== undefined),
    [aiByDay]
  );

  const aiCards = useMemo(() => {
    if (!ai) return [];
    return [
      {
        label: 'KI-Anrufe',
        value: fmtInt(ai.counts.aiCalls),
        helper: 'Gespräche mit dem KI-Agenten',
      },
      {
        label: 'Selbst gelöst',
        value: fmtPct(ai.percentages.resolutionRate),
        helper: `${fmtInt(ai.counts.resolved)} ohne Mensch abgeschlossen`,
      },
      {
        label: 'An Mensch übergeben',
        value: fmtPct(ai.percentages.transferRate),
        helper: `${fmtInt(ai.counts.transferred)} Weiterleitungen`,
      },
      {
        label: 'Rückruf angefordert',
        value: fmtPct(ai.percentages.callbackRate),
        helper: `${fmtInt(ai.counts.callbackRequested)} Rückrufwünsche`,
      },
      {
        label: 'Anteil KI am Volumen',
        value: fmtPct(ai.percentages.aiHandledShare),
        helper: 'Nur-KI-Anrufe an allen Anrufen',
      },
      {
        label: 'Ø Dauer KI',
        value: fmtSec(ai.duration.aiAverageSeconds),
        helper: `Mensch: ${fmtSec(ai.duration.humanAverageSeconds)}`,
      },
      {
        label: 'Reservierungen',
        value: fmtInt(ai.booking.reservationCreated),
        helper: `${fmtPct(ai.booking.completionRate)} der ${fmtInt(ai.booking.attempts)} Buchungsversuche`,
      },
      {
        label: 'Kosten je Anruf',
        value: `${fmtInt(ai.cost.creditsPerCall)} Credits`,
        // The average is computed over the calls that actually carry a cost figure, not over all
        // AI calls — saying so keeps the number from reading as a per-call cost for everything.
        helper:
          ai.cost.costedCalls === undefined
            ? `Je Reservierung: ${fmtInt(ai.cost.creditsPerReservation)} Credits`
            : `Basis: ${fmtInt(ai.cost.costedCalls)} von ${fmtInt(ai.counts.aiCalls)} Anrufen mit Kostendaten`,
      },
      ...(aiSentiment && aiSentiment.ratedCalls > 0
        ? [
            {
              label: 'Stimmung',
              value:
                aiSentiment.averageScore === null || aiSentiment.averageScore === undefined
                  ? `${fmtPct(aiSentiment.positiveShare)} positiv`
                  : aiSentiment.averageScore.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }),
              helper: `${fmtPct(aiSentiment.negativeShare)} negativ · Basis: ${fmtInt(aiSentiment.ratedCalls)} Anrufe`,
            },
          ]
        : []),
    ];
  }, [ai, aiSentiment]);

  // Gesprächsprotokolle, aus demselben Endpunkt: dieselben Anrufe aus Sicht dessen, was der
  // Mitarbeiter notiert hat.
  const protocolStats = useMemo(
    () => (isFeatureEnabled('callProtocol') ? stats?.protocols || null : null),
    [stats]
  );
  const protocolCards = useMemo(() => {
    if (!protocolStats) return [];
    return [
      {
        label: 'Protokolle',
        value: fmtInt(protocolStats.counts.total),
        helper: `${fmtInt(protocolStats.counts.completed)} abgeschlossen`,
      },
      {
        label: 'Abschlussquote',
        value: fmtPct(protocolStats.percentages.completionRate),
        helper: `${fmtInt(protocolStats.counts.draft)} noch Entwurf`,
      },
      {
        label: 'Offene Wiedervorlagen',
        value: fmtInt(protocolStats.counts.followUpOpen),
        helper: `von ${fmtInt(protocolStats.counts.followUpRequired)} insgesamt`,
      },
      {
        label: 'Mit Anliegen erfasst',
        value: fmtPct(protocolStats.percentages.categoryCoverage),
        helper: `${fmtInt(protocolStats.counts.withoutCategory)} ohne Anliegen`,
      },
      // Only from cache version v12 on; an older cached statistic simply omits the card.
      ...(protocolStats.percentages.bookingCoverage === undefined
        ? []
        : [
            {
              label: 'Buchung verknüpft',
              value: fmtPct(protocolStats.percentages.bookingCoverage),
              helper:
                protocolStats.counts.withUnresolvedBooking > 0
                  ? `${fmtInt(protocolStats.counts.withUnresolvedBooking)} Nummern ohne passende Buchung`
                  : `${fmtInt(protocolStats.counts.withResolvedBooking)} Protokolle mit gefundener Buchung`,
            },
          ]),
    ];
  }, [protocolStats]);

  const phoneCards = useMemo(() => {
    if (!phoneSummary) return [];
    return [
      {
        label: 'Eingegangene Anrufe',
        value: fmtInt(phoneSummary.callsReceived),
        helper: 'Anrufe mit Abschluss im Zeitraum',
      },
      {
        label: 'Eingegangene Anrufe (unique)',
        value: fmtInt(phoneSummary.callsReceivedUnique),
        helper: 'Unterschiedliche Anrufer',
      },
      {
        label: 'Offene Rückrufe (Tagesende)',
        value: fmtInt(phoneSummary.callbacksOpen),
        helper: 'Status: waitingForCallback',
      },
      {
        label: 'Durchgeführte Rückrufe',
        value: fmtInt(phoneSummary.callbackCount),
        helper: 'Summe ausgehender Rückrufaktionen',
      },
      {
        label: 'Direkt beantwortete Anrufe',
        value: fmtInt(phoneSummary.callsDirectlyAnswered),
        helper: 'successfulOnFirstCall',
      },
      {
        label: 'Anteil direkt beantwortet',
        value: fmtPct(phoneSummary.callsDirectlyAnsweredQuote),
        helper: 'direkt beantwortet / eingegangen',
      },
      {
        label: 'Ø Anrufdauer',
        value: fmtSec(phoneSummary.callsDurationAverage),
        helper: 'Gesprächszeit pro erfolgreich geführtem Anruf',
      },
      {
        label: 'Ø Wartedauer',
        value: fmtSec(phoneSummary.callsWaittimeAverage),
        helper: 'Klingelzeit vor Annahme',
      },
    ];
  }, [phoneSummary]);

  const phoneChartData = useMemo(() => {
    return phoneByDay.map(p => ({
      ...p,
      label: dayjs(p.date).isValid() ? dayjs(p.date).format('DD.MM.') : String(p.date || ''),
    }));
  }, [phoneByDay]);

  const phoneByWeekday = useMemo(
    () => (Array.isArray(stats?.phoneByWeekday) ? stats.phoneByWeekday : []),
    [stats]
  );
  const phoneByHour = useMemo(
    () => (Array.isArray(stats?.phoneByHour) ? stats.phoneByHour : []),
    [stats]
  );
  const phoneByWeekdayHour = useMemo(
    () => (Array.isArray(stats?.phoneByWeekdayHour) ? stats.phoneByWeekdayHour : []),
    [stats]
  );

  // Absolutwert fuer die Heatmap-Intensitaet.
  const heatmapMax = useMemo(() => {
    let max = 0;
    for (const row of phoneByWeekdayHour) {
      for (const h of row.hours || []) {
        if (h.callsReceived > max) max = h.callsReceived;
      }
    }
    return max;
  }, [phoneByWeekdayHour]);

  const heatmapHasData = heatmapMax > 0;

  return (
    <div style={getStatisticsPageRootStyle(theme)}>
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      onApply={applyFilters}
      applyDisabled={isFetching}
      applyExtraActions={
        <Button
          variant='outlined'
          onClick={() => setChartType(prev => (prev === 'line' ? 'bar' : 'line'))}
          sx={{ minWidth: 56, minHeight: 56, padding: 0, borderRadius: 2 }}
          title={chartType === 'line' ? 'Balken' : 'Linie'}
        >
          {chartType === 'line' ? '▦' : '〰'}
        </Button>
      }
      status={status}
      isStale={isStale}
      cacheComputedAt={cacheComputedAt}
      cacheExpiresAt={cacheExpiresAt}
      job={job}
      error={error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedEntryId}
      onSelectEntry={handleSelectEntry}
      entriesLoading={entriesLoading}
      onRefresh={refreshStatistic}
      hasPayload={!!stats}
    >
      {ccStaffTotals && (
        <Box component='section' sx={{ mb: 3 }}>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600, mb: 2 }}>
            Personalkosten (Call Center)
          </Typography>
          <StaffCostEstimateNotice
            assumptions={ccStaffCost.payload?.meta?.assumptions}
            sx={{ mb: 2 }}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
            }}
          >
            {[
              {
                label: 'Personalkosten',
                value: fmtEuro(ccStaffTotals.staffCost),
                helper: 'inkl. Arbeitgeber-Faktor (rollenbasiert)',
              },
              {
                label: 'Arbeitsstunden',
                value: `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(ccStaffTotals.hours || 0)} h`,
                helper: 'Produktive Stunden im Zeitraum',
              },
              {
                label: 'Mitarbeiter',
                value: ccStaffTotals.headcount ?? 0,
                helper: 'Personen mit Arbeitszeit',
              },
              {
                label: 'Ø Kosten / Stunde',
                value: ccStaffTotals.hours > 0 ? fmtEuro(ccStaffTotals.staffCost / ccStaffTotals.hours) : '–',
                helper: 'Durchschnitt je Personalstunde',
              },
            ].map(card => (
              <Box
                key={card.label}
                component='article'
                sx={{
                  background: statSurfaceBg,
                  borderRadius: '8px',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                  minWidth: 0,
                }}
              >
                <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>{card.label}</span>
                <strong style={{ fontSize: '1.4rem', overflowWrap: 'anywhere' }}>{card.value}</strong>
                <small style={{ color: kpiHelperColor }}>{card.helper}</small>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {!isFetching && stats && phoneSummary && (
        <Box component='section' sx={{ mb: 3 }}>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600, mb: 2 }}>
            Anrufvolumen
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              mb: 3,
            }}
          >
            {phoneCards.map(card => (
              <Box
                component='article'
                key={`phone-${card.label}`}
                sx={{
                  bgcolor: statSurfaceBg,
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography component='span' variant='body2' sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  {card.label}
                </Typography>
                <Typography component='strong' sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {card.value}
                </Typography>
                <Typography component='small' variant='caption' sx={{ color: 'text.secondary' }}>
                  {card.helper}
                </Typography>
              </Box>
            ))}
          </Box>

          {phoneChartData.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                mb: 2,
              }}
            >
              <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 300 }}>
                <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                  Anrufvolumen im Zeitverlauf
                </Typography>
                <ResponsiveContainer width='100%' height='85%'>
                  <LineChart data={phoneChartData}>
                    <CartesianGrid stroke={chartGridStroke} />
                    <XAxis dataKey='label' stroke={axisStroke} />
                    <YAxis stroke={axisStroke} allowDecimals={false} />
                    <Tooltip {...chartTooltipProps} />
                    <Legend />
                    <Line
                      type='monotone'
                      dataKey='callsReceived'
                      name='Eingegangen'
                      stroke='#42a5f5'
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type='monotone'
                      dataKey='callsDirectlyAnswered'
                      name='Direkt beantwortet'
                      stroke='#4caf50'
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>

              <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 300 }}>
                <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                  Dauer & Wartezeit (Sekunden)
                </Typography>
                <ResponsiveContainer width='100%' height='85%'>
                  <LineChart data={phoneChartData}>
                    <CartesianGrid stroke={chartGridStroke} />
                    <XAxis dataKey='label' stroke={axisStroke} />
                    <YAxis stroke={axisStroke} allowDecimals={false} />
                    <Tooltip {...chartTooltipProps} />
                    <Legend />
                    <Line
                      type='monotone'
                      dataKey='callsDurationAverage'
                      name='Ø Anrufdauer'
                      stroke='#ff9800'
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type='monotone'
                      dataKey='callsWaittimeAverage'
                      name='Ø Wartedauer'
                      stroke='#f44336'
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
          )}

          {phoneByDay.length > 0 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={plainAccordionSx}
              TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Tägliche Werte (Tabelle)</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tag</TableCell>
                        <TableCell align='right'>Eingegangen</TableCell>
                        <TableCell align='right'>Unique</TableCell>
                        <TableCell align='right'>Offene Rückrufe</TableCell>
                        <TableCell align='right'>Rückrufe</TableCell>
                        <TableCell align='right'>Direkt beantw.</TableCell>
                        <TableCell align='right'>Direkt beantw. %</TableCell>
                        <TableCell align='right'>Ø Dauer</TableCell>
                        <TableCell align='right'>Ø Wartezeit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {phoneByDay.map(row => (
                        <TableRow key={row.date}>
                          <TableCell>{dayjs(row.date).format('dd, DD.MM.YYYY')}</TableCell>
                          <TableCell align='right'>{fmtInt(row.callsReceived)}</TableCell>
                          <TableCell align='right'>{fmtInt(row.callsReceivedUnique)}</TableCell>
                          <TableCell align='right'>{fmtInt(row.callbacksOpen)}</TableCell>
                          <TableCell align='right'>{fmtInt(row.callbackCount)}</TableCell>
                          <TableCell align='right'>
                            {fmtInt(row.callsDirectlyAnswered)}
                          </TableCell>
                          <TableCell align='right'>
                            {fmtPct(row.callsDirectlyAnsweredQuote)}
                          </TableCell>
                          <TableCell align='right'>{fmtSec(row.callsDurationAverage)}</TableCell>
                          <TableCell align='right'>{fmtSec(row.callsWaittimeAverage)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {(phoneByWeekday.length > 0 || phoneByHour.length > 0) && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                mt: 2,
              }}
            >
              {phoneByWeekday.length > 0 && (
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
                  <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                    Anrufvolumen nach Wochentag
                  </Typography>
                  <ResponsiveContainer width='100%' height='88%'>
                    <ComposedChart data={phoneByWeekday}>
                      <CartesianGrid stroke={chartGridStroke} vertical={false} />
                      <XAxis dataKey='label' stroke={axisStroke} />
                      <YAxis
                        yAxisId='left'
                        stroke='#42a5f5'
                        allowDecimals={false}
                      />
                      <YAxis
                        yAxisId='right'
                        orientation='right'
                        stroke='#4caf50'
                        tickFormatter={v => `${v}%`}
                        domain={[0, 100]}
                      />
                      <Tooltip
                        cursor={barTooltipCursor}
                        {...chartTooltipProps}
                        formatter={(value, name) =>
                          name === 'Direkt beantwortet %'
                            ? [`${fmtPct(value)}`, name]
                            : [fmtInt(value), name]
                        }
                      />
                      <Legend />
                      <Bar
                        yAxisId='left'
                        dataKey='callsReceived'
                        name='Eingegangen'
                        fill='#42a5f5'
                      />
                      <Line
                        yAxisId='right'
                        type='monotone'
                        dataKey='callsDirectlyAnsweredQuote'
                        name='Direkt beantwortet %'
                        stroke='#4caf50'
                        strokeWidth={2}
                        dot
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Paper>
              )}

              {phoneByHour.length > 0 && (
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
                  <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                    Anrufvolumen nach Tageszeit
                  </Typography>
                  <ResponsiveContainer width='100%' height='88%'>
                    <ComposedChart data={phoneByHour}>
                      <CartesianGrid stroke={chartGridStroke} vertical={false} />
                      <XAxis dataKey='label' stroke={axisStroke} interval={1} fontSize={10} />
                      <YAxis
                        yAxisId='left'
                        stroke='#42a5f5'
                        allowDecimals={false}
                      />
                      <YAxis
                        yAxisId='right'
                        orientation='right'
                        stroke='#f44336'
                        tickFormatter={v => `${v}s`}
                      />
                      <Tooltip
                        cursor={barTooltipCursor}
                        {...chartTooltipProps}
                        formatter={(value, name) =>
                          name === 'Ø Wartedauer'
                            ? [fmtSec(value), name]
                            : [fmtInt(value), name]
                        }
                      />
                      <Legend />
                      <Bar
                        yAxisId='left'
                        dataKey='callsReceived'
                        name='Eingegangen'
                        fill='#42a5f5'
                      />
                      <Line
                        yAxisId='right'
                        type='monotone'
                        dataKey='callsWaittimeAverage'
                        name='Ø Wartedauer'
                        stroke='#f44336'
                        strokeWidth={2}
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Paper>
              )}
            </Box>
          )}

          {heatmapHasData && (
            <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, mt: 2 }}>
              <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                Heatmap: Wochentag × Stunde (eingegangene Anrufe)
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '48px repeat(24, minmax(22px, 1fr))',
                    gap: 0.25,
                    minWidth: 24 * 22 + 48,
                  }}
                >
                  <Box />
                  {Array.from({ length: 24 }, (_, h) => (
                    <Box
                      key={`hhead-${h}`}
                      sx={{
                        fontSize: '0.65rem',
                        color: 'text.secondary',
                        textAlign: 'center',
                        lineHeight: 1,
                      }}
                    >
                      {h % 2 === 0 ? String(h).padStart(2, '0') : ''}
                    </Box>
                  ))}
                  {phoneByWeekdayHour.map(row => (
                    <React.Fragment key={`wd-${row.weekday}`}>
                      <Box
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          pr: 0.5,
                        }}
                      >
                        {row.label}
                      </Box>
                      {(row.hours || []).map(h => {
                        const v = h.callsReceived || 0;
                        const intensity = heatmapMax > 0 ? v / heatmapMax : 0;
                        const alpha = v === 0 ? 0.06 : 0.18 + intensity * 0.82;
                        return (
                          <Box
                            key={`wd-${row.weekday}-h-${h.hour}`}
                            title={`${row.label} ${String(h.hour).padStart(2, '0')}:00 – ${fmtInt(v)} Anrufe, Ø Warten ${fmtSec(h.callsWaittimeAverage)}`}
                            sx={{
                              height: 22,
                              borderRadius: 0.5,
                              bgcolor: `rgba(66, 165, 245, ${alpha})`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.6rem',
                              color:
                                intensity > 0.55
                                  ? theme.palette.common.white
                                  : theme.palette.mode === 'light'
                                    ? theme.palette.text.secondary
                                    : theme.palette.grey[400],
                            }}
                          >
                            {v > 0 && intensity > 0.35 ? v : ''}
                          </Box>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
              <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                Farbintensität = eingegangene Anrufe im Slot. Max: {fmtInt(heatmapMax)}.
              </Typography>
            </Paper>
          )}

          <Box sx={{ mt: 2 }}>
            <StatisticMatrixTables
              title='Tabellen pro Kennzahl (wie alte Buchungsstatistik)'
              matrix={stats?.phoneMatrix}
              metrics={CALL_VOLUME_METRICS}
            />
          </Box>
        </Box>
      )}

      {!isFetching && stats && ai && (
        <Box component='section' sx={{ mb: 3 }}>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600, mb: 2 }}>
            KI-Telefonie
          </Typography>

          {ai.percentages.analysisCoverage < 100 && (
            <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
              {`Auswertung von ${fmtInt(ai.counts.analysed)} der ${fmtInt(ai.counts.aiCalls)} Anrufe vorhanden (${fmtPct(ai.percentages.analysisCoverage)}). `}
              {`Sentiment und Kosten beziehen sich nur auf diese Anrufe.`}
              {ai.counts.syncFailed > 0 ? ` ${fmtInt(ai.counts.syncFailed)} Abgleiche fehlgeschlagen.` : ''}
            </Typography>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              mb: 3,
            }}
          >
            {aiCards.map(card => (
              <Box
                component='article'
                key={`ai-${card.label}`}
                sx={{
                  bgcolor: statSurfaceBg,
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography component='span' variant='body2' sx={{ color: kpiLabelColor, fontSize: '0.85rem' }}>
                  {card.label}
                </Typography>
                <Typography component='strong' sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {card.value}
                </Typography>
                <Typography component='small' variant='caption' sx={{ color: kpiHelperColor }}>
                  {card.helper}
                </Typography>
              </Box>
            ))}
          </Box>

          {aiByDay.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                KI-Anrufe pro Tag
              </Typography>
              <ResponsiveContainer width='100%' height={280}>
                <BarChart data={aiByDay}>
                  <CartesianGrid strokeDasharray='3 3' stroke={chartGridStroke} />
                  <XAxis dataKey='date' stroke={axisStroke} />
                  <YAxis allowDecimals={false} stroke={axisStroke} />
                  <Tooltip {...chartTooltipProps} cursor={barTooltipCursor} />
                  <Legend {...chartLegendBarProps} />
                  <Bar dataKey='resolved' name='Selbst gelöst' stackId='ai' fill={chartPaletteColors[0]} />
                  <Bar dataKey='transferred' name='An Mensch' stackId='ai' fill={chartPaletteColors[2]} />
                  <Bar dataKey='callback' name='Rückruf' stackId='ai' fill={chartPaletteColors[1]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              mb: 2,
            }}
          >
            {aiSplitData.length > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                  Aufteilung des Anrufvolumens
                </Typography>
                <ResponsiveContainer width='100%' height={260}>
                  <PieChart>
                    <Pie data={aiSplitData} dataKey='value' nameKey='name' outerRadius={90} label>
                      {aiSplitData.map((entry, idx) => (
                        <Cell key={`ai-split-${entry.name}`} fill={chartPaletteColors[idx % chartPaletteColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip {...chartTooltipProps} />
                    <Legend {...chartLegendPieProps} />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            )}

            {aiOutcomePieData.length > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                  Gesprächsausgang
                </Typography>
                <ResponsiveContainer width='100%' height={260}>
                  <PieChart>
                    <Pie data={aiOutcomePieData} dataKey='value' nameKey='name' outerRadius={90} label>
                      {aiOutcomePieData.map((entry, idx) => (
                        <Cell key={`ai-outcome-${entry.name}`} fill={chartPaletteColors[idx % chartPaletteColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip {...chartTooltipProps} />
                    <Legend {...chartLegendPieProps} />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            )}
          </Box>

          {aiSentiment && aiSentiment.ratedCalls > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
                Stimmung der Anrufer
              </Typography>
              <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                {`Bewertet für ${fmtInt(aiSentiment.ratedCalls)} von ${fmtInt(ai.counts.aiCalls)} KI-Anrufen. `}
                {aiSentiment.thresholds
                  ? `Werte bis ${aiSentiment.thresholds.negativeMax} gelten als negativ, ab ${aiSentiment.thresholds.positiveMin} als positiv.`
                  : ''}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                }}
              >
                {aiSentimentBuckets.length > 0 && (
                  <Box>
                    <ResponsiveContainer width='100%' height={220}>
                      <PieChart>
                        <Pie
                          data={aiSentimentBuckets.map(b => ({ name: b.label, value: b.count }))}
                          dataKey='value'
                          nameKey='name'
                          outerRadius={80}
                          label
                        >
                          {aiSentimentBuckets.map((entry, idx) => (
                            <Cell
                              key={`ai-sentiment-${entry.bucket}`}
                              fill={chartPaletteColors[idx % chartPaletteColors.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip {...chartTooltipProps} />
                        <Legend {...chartLegendPieProps} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                )}

                {aiSentimentTrend.length > 1 && (
                  <Box>
                    <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                      Ø Stimmungswert pro Tag
                    </Typography>
                    <ResponsiveContainer width='100%' height={200}>
                      <LineChart data={aiSentimentTrend}>
                        <CartesianGrid strokeDasharray='3 3' stroke={chartGridStroke} />
                        <XAxis dataKey='date' stroke={axisStroke} />
                        <YAxis domain={[-1, 1]} stroke={axisStroke} />
                        <Tooltip {...chartTooltipProps} />
                        <Line
                          type='monotone'
                          dataKey='sentimentScore'
                          name='Ø Stimmung'
                          stroke={chartPaletteColors[0]}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>

              {aiSentimentByOutcome.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                    Stimmung und Gesprächsausgang
                  </Typography>
                  <TableContainer>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Stimmung</TableCell>
                          <TableCell align='right'>Anrufe</TableCell>
                          <TableCell align='right'>Selbst gelöst</TableCell>
                          <TableCell align='right'>An Mensch abgegeben</TableCell>
                          <TableCell align='right'>Ø Dauer</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {aiSentimentByOutcome.map(row => (
                          <TableRow key={`ai-sentiment-outcome-${row.bucket}`} sx={tableRowBorderTop}>
                            <TableCell>{row.label}</TableCell>
                            <TableCell align='right'>{fmtInt(row.count)}</TableCell>
                            <TableCell align='right'>{fmtPct(row.resolutionRate)}</TableCell>
                            <TableCell align='right'>{fmtPct(row.escalationRate)}</TableCell>
                            <TableCell align='right'>{fmtSec(row.averageDurationSeconds)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                    „An Mensch abgegeben“ fasst Weiterleitungen und Rückrufwünsche zusammen. Liegt der
                    Wert bei negativen Anrufen deutlich höher, verliert der KI-Agent genau die
                    unzufriedenen Anrufer.
                  </Typography>
                  {aiSentiment.unhappyUnescalated > 0 && (
                    <Typography variant='caption' sx={{ color: 'warning.main', display: 'block', mt: 0.5 }}>
                      {`${fmtInt(aiSentiment.unhappyUnescalated)} negativ bewertete Anrufe wurden weder übergeben noch als Rückruf erfasst — diese Gespräche hat niemand nachgehört.`}
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          )}

          {aiTopics.length > 0 && (
            <Accordion sx={plainAccordionSx} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                  Themen der Anrufe
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Thema</TableCell>
                        <TableCell align='right'>Anrufe</TableCell>
                        <TableCell align='right'>Anteil</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {aiTopics.map(topic => (
                        <TableRow key={`ai-topic-${topic.topic}`} sx={tableRowBorderTop}>
                          <TableCell>{topic.topic}</TableCell>
                          <TableCell align='right'>{fmtInt(topic.count)}</TableCell>
                          <TableCell align='right'>{fmtPct(topic.percent)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                  Ein Anruf kann mehrere Themen haben; die Anteile summieren sich deshalb nicht auf 100 %.
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      {!isFetching && stats && protocolStats && (
        <Box component='section' sx={{ mb: 3 }}>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600, mb: 2 }}>
            Gesprächsprotokolle
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              mb: 3,
            }}
          >
            {protocolCards.map(card => (
              <Box
                component='article'
                key={`protocol-${card.label}`}
                sx={{
                  bgcolor: statSurfaceBg,
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography component='span' variant='body2' sx={{ color: kpiLabelColor, fontSize: '0.85rem' }}>
                  {card.label}
                </Typography>
                <Typography component='strong' sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {card.value}
                </Typography>
                <Typography component='small' variant='caption' sx={{ color: kpiHelperColor }}>
                  {card.helper}
                </Typography>
              </Box>
            ))}
          </Box>

          {protocolStats.counts.withoutCategory > 0 && (
            <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
              {`${fmtInt(protocolStats.counts.withoutCategory)} Protokolle tragen kein Anliegen — die Verteilung unten bezieht sich nur auf die übrigen.`}
            </Typography>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {[
              { title: 'Nach Anliegen', rows: protocolStats.byCategory, key: 'category' },
              { title: 'Nach Ergebnis', rows: protocolStats.byOutcome, key: 'outcome' },
              { title: 'Nach Herkunft', rows: protocolStats.bySource, key: 'source' },
            ].map(table => (
              <Paper key={table.title} sx={{ p: 2 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                  {table.title}
                </Typography>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Bezeichnung</TableCell>
                        <TableCell align='right'>Anzahl</TableCell>
                        <TableCell align='right'>Anteil</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(table.rows || []).map(row => (
                        <TableRow key={`${table.key}-${row[table.key]}`} sx={tableRowBorderTop}>
                          <TableCell>{row.label}</TableCell>
                          <TableCell align='right'>{fmtInt(row.count)}</TableCell>
                          <TableCell align='right'>{fmtPct(row.percent)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}

            <Paper sx={{ p: 2 }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                Je Mitarbeiter
              </Typography>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Mitarbeiter</TableCell>
                      <TableCell align='right'>Protokolle</TableCell>
                      <TableCell align='right'>Abgeschlossen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(protocolStats.byUser || []).map(row => (
                      <TableRow key={row.userId || 'unassigned'} sx={tableRowBorderTop}>
                        <TableCell>{row.displayName}</TableCell>
                        <TableCell align='right'>{fmtInt(row.count)}</TableCell>
                        <TableCell align='right'>{fmtInt(row.completed)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </Box>
      )}

      {!isFetching && stats && ccAdded && (
        <Box component='section' sx={{ mb: 3 }}>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600, mb: 2 }}>
            Einbuchungen durch Call Center
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              mb: 3,
            }}
          >
            {[
              {
                label: 'Neue Buchungen',
                value: fmtInt(ccAdded.count),
                helper: 'meta.createdBy = CC-Mitarbeiter',
              },
              {
                label: 'Gesamtvolumen',
                value: fmtEuro(ccAdded.total),
                helper: 'Summe totalAmount',
              },
              {
                label: 'Ø Buchungswert',
                value: fmtEuro(ccAdded.averageValue),
                helper: 'Gesamt / Anzahl',
              },
              {
                label: 'Aktive CC-Einbucher',
                value: fmtInt(ccAddedByUser.length),
                helper: 'Unterschiedliche Mitarbeiter',
              },
            ].map(card => (
              <Box
                component='article'
                key={`cc-added-${card.label}`}
                sx={{
                  bgcolor: statSurfaceBg,
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography component='span' variant='body2' sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  {card.label}
                </Typography>
                <Typography component='strong' sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {card.value}
                </Typography>
                <Typography component='small' variant='caption' sx={{ color: 'text.secondary' }}>
                  {card.helper}
                </Typography>
              </Box>
            ))}
          </Box>

          {ccAddedChartData.length > 0 && ccAdded.count > 0 && (
            <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320, mb: 2 }}>
              <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                Einbuchungen im Zeitverlauf
              </Typography>
              <ResponsiveContainer width='100%' height='88%'>
                <ComposedChart data={ccAddedChartData}>
                  <CartesianGrid stroke={chartGridStroke} />
                  <XAxis dataKey='label' stroke={axisStroke} />
                  <YAxis
                    yAxisId='left'
                    stroke='#4caf50'
                    allowDecimals={false}
                  />
                  <YAxis
                    yAxisId='right'
                    orientation='right'
                    stroke='#ff9800'
                    tickFormatter={v =>
                      new Intl.NumberFormat('de-DE', {
                        notation: 'compact',
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 1,
                      }).format(Number(v || 0))
                    }
                  />
                  <Tooltip
                    {...chartTooltipProps}
                    formatter={(value, name) =>
                      name === 'Volumen' ? [fmtEuro(value), name] : [fmtInt(value), name]
                    }
                  />
                  <Legend />
                  <Bar
                    yAxisId='left'
                    dataKey='count'
                    name='Anzahl'
                    fill='#4caf50'
                  />
                  <Line
                    yAxisId='right'
                    type='monotone'
                    dataKey='total'
                    name='Volumen'
                    stroke='#ff9800'
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {(ccAddedByWeekday.length > 0 || ccAddedByHour.length > 0) && ccAdded.count > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                mb: 2,
              }}
            >
              {ccAddedByWeekday.length > 0 && (
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 280 }}>
                  <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                    Einbuchungen nach Wochentag
                  </Typography>
                  <ResponsiveContainer width='100%' height='88%'>
                    <BarChart data={ccAddedByWeekday}>
                      <CartesianGrid stroke={chartGridStroke} vertical={false} />
                      <XAxis dataKey='label' stroke={axisStroke} />
                      <YAxis stroke={axisStroke} allowDecimals={false} />
                      <Tooltip
                        cursor={barTooltipCursor}
                        {...chartTooltipProps}
                        formatter={(value, name) =>
                          name === 'Volumen' ? [fmtEuro(value), name] : [fmtInt(value), name]
                        }
                      />
                      <Legend />
                      <Bar dataKey='count' name='Anzahl' fill='#4caf50' />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              )}

              {ccAddedByHour.length > 0 && (
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 280 }}>
                  <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                    Einbuchungen nach Tageszeit
                  </Typography>
                  <ResponsiveContainer width='100%' height='88%'>
                    <BarChart data={ccAddedByHour}>
                      <CartesianGrid stroke={chartGridStroke} vertical={false} />
                      <XAxis dataKey='label' stroke={axisStroke} interval={1} fontSize={10} />
                      <YAxis stroke={axisStroke} allowDecimals={false} />
                      <Tooltip
                        cursor={barTooltipCursor}
                        {...chartTooltipProps}
                        formatter={(value, name) =>
                          name === 'Volumen' ? [fmtEuro(value), name] : [fmtInt(value), name]
                        }
                      />
                      <Legend />
                      <Bar dataKey='count' name='Anzahl' fill='#4caf50' />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              )}
            </Box>
          )}

          {ccAddedByUser.length > 0 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{ ...plainAccordionSx, mb: 1 }}
              TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Einbuchungen pro CC-Mitarbeiter ({ccAddedByUser.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Mitarbeiter</TableCell>
                        <TableCell>Rollen</TableCell>
                        <TableCell align='right'>Anzahl</TableCell>
                        <TableCell align='right'>Volumen</TableCell>
                        <TableCell align='right'>Ø Buchungswert</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ccAddedByUser.map(u => (
                        <TableRow key={u.userId}>
                          <TableCell>
                            <div>
                              <strong>{u.displayName}</strong>
                              <Box component='div' sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                @{u.username}
                              </Box>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                              {(u.roles || []).map(role => (
                                <Chip
                                  key={`${u.userId}-${role}`}
                                  label={role === 'ccManager' ? 'Manager' : 'Mitarbeiter'}
                                  size='small'
                                  color={role === 'ccManager' ? 'primary' : 'default'}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell align='right'>{fmtInt(u.count)}</TableCell>
                          <TableCell align='right'>{fmtEuro(u.total)}</TableCell>
                          <TableCell align='right'>
                            {u.count > 0 ? fmtEuro(u.total / u.count) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {ccAddedByBranch.length > 0 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={plainAccordionSx}
              TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Einbuchungen pro Standort ({ccAddedByBranch.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Standort</TableCell>
                        <TableCell align='right'>Anzahl</TableCell>
                        <TableCell align='right'>Volumen</TableCell>
                        <TableCell align='right'>Ø Buchungswert</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ccAddedByBranch.map(b => (
                        <TableRow key={b.branchId}>
                          <TableCell>{b.branchName}</TableCell>
                          <TableCell align='right'>{fmtInt(b.count)}</TableCell>
                          <TableCell align='right'>{fmtEuro(b.total)}</TableCell>
                          <TableCell align='right'>
                            {b.count > 0 ? fmtEuro(b.total / b.count) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {ccAdded.count === 0 && (
            <Typography sx={{ color: 'text.secondary' }}>
              Im gewählten Zeitraum wurden keine Einbuchungen durch Call-Center-Mitarbeiter erfasst.
            </Typography>
          )}
        </Box>
      )}

      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 1 }}>
          Kennzahlen
        </Typography>
        {stats && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
            }}
          >
            {cards.map(card => (
              <article
                key={card.label}
                style={{
                  backgroundColor: statSurfaceBg,
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>{card.label}</span>
                <strong style={{ fontSize: '1.5rem' }}>{card.value}</strong>
                <small style={{ color: kpiHelperColor }}>{card.helper}</small>
              </article>
            ))}
          </div>
        )}
      </section>

      {stats && (
        <section>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent='space-between'
            sx={{ mb: 1 }}
          >
            <Typography variant='h6' component='h2' sx={{ m: 0 }}>
              Call Center Aktivität
            </Typography>
          </Stack>
          <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 360 }}>
            <ResponsiveContainer width='100%' height='100%'>
              {chartType === 'line' ? (
                <LineChart data={chartData}>
                  <CartesianGrid stroke={chartGridStroke} />
                  <XAxis dataKey='label' stroke={axisStroke} />
                  <YAxis stroke={axisStroke} allowDecimals={false} />
                  <Tooltip {...chartTooltipProps} />
                  <Legend {...chartLegendBarProps} />
                  <Line
                    type='monotone'
                    dataKey='changeCount'
                    name='Änderungen'
                    stroke={activityLineColors.changes}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type='monotone'
                    dataKey='bookingCount'
                    name='Bearbeitete Buchungen'
                    stroke={activityLineColors.bookings}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              ) : (
                <BarChart data={chartData}>
                  <CartesianGrid stroke={chartGridStroke} vertical={false} />
                  <XAxis dataKey='label' stroke={axisStroke} />
                  <YAxis stroke={axisStroke} allowDecimals={false} />
                  <Tooltip cursor={barTooltipCursor} {...chartTooltipProps} />
                  <Legend {...chartLegendBarProps} />
                  <Bar dataKey='changeCount' name='Änderungen' fill={activityLineColors.changes} />
                  <Bar dataKey='bookingCount' name='Bearbeitete Buchungen' fill={activityLineColors.bookings} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </Paper>
        </section>
      )}

      {stats && hourlyData.length > 0 && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Änderungsaktivität nach Tageszeit
          </Typography>
          <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 300 }}>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart data={hourlyData}>
                <CartesianGrid stroke={chartGridStroke} vertical={false} />
                <XAxis dataKey='label' stroke={axisStroke} interval={1} fontSize={10} />
                <YAxis stroke={axisStroke} allowDecimals={false} />
                <Tooltip cursor={barTooltipCursor} {...chartTooltipProps} />
                <Bar dataKey='count' name='Änderungen' fill={activityLineColors.hourly} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </section>
      )}

      {stats && (changeTypePieData.length > 0 || userPieData.length > 0) && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Verteilung
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              alignItems: 'stretch',
            }}
          >
            {changeTypePieData.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  bgcolor: statSurfaceBg,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant='subtitle1' component='h3' sx={{ mt: 0, mb: 1.5, fontWeight: 600 }}>
                  Art der Änderungen
                </Typography>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={changeTypePieData}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey='value'
                        stroke='none'
                      >
                        {changeTypePieData.map((entry, idx) => (
                          <Cell
                            key={`${entry.name}-${idx}`}
                            fill={chartPaletteColors[idx % chartPaletteColors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={value => String(value)} {...chartTooltipProps} />
                      <Legend {...chartLegendPieProps} verticalAlign='bottom' height={48} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Paper>
            )}

            {userPieData.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  bgcolor: statSurfaceBg,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant='subtitle1' component='h3' sx={{ mt: 0, mb: 1.5, fontWeight: 600 }}>
                  Änderungen pro Mitarbeiter
                </Typography>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={userPieData}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey='value'
                        stroke='none'
                      >
                        {userPieData.map((entry, idx) => (
                          <Cell
                            key={`${entry.name}-${idx}`}
                            fill={chartPaletteColors[idx % chartPaletteColors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={value => String(value)} {...chartTooltipProps} />
                      <Legend {...chartLegendPieProps} verticalAlign='bottom' height={48} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Paper>
            )}
          </Box>
        </section>
      )}

      {stats && stats.userStats && stats.userStats.length > 0 && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Mitarbeiter-Leistung
          </Typography>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              bgcolor: statSurfaceBg,
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: '640px',
                }}
              >
                <thead>
                  <tr>
                    <th style={htmlThStyle()}>Mitarbeiter</th>
                    <th style={htmlThStyle()}>Rollen</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Änderungen</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Buchungen bearbeitet</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Ø Änderungen/Buchung</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.userStats.map(user => (
                    <tr key={user.userId}>
                      <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                        <div>
                          <strong>{user.displayName}</strong>
                          <div style={{ fontSize: '0.8rem', color: kpiHelperColor }}>@{user.username}</div>
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                        <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                          {user.roles.map(role => (
                            <Chip
                              key={role}
                              label={role === 'ccManager' ? 'Manager' : 'Mitarbeiter'}
                              size='small'
                              color={role === 'ccManager' ? 'primary' : 'default'}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Stack>
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        <strong>{user.changeCount}</strong>
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {user.bookingsModified}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {user.bookingsModified > 0
                          ? (user.changeCount / user.bookingsModified).toFixed(1)
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Paper>
        </section>
      )}

      {stats && stats.changeTypes && stats.changeTypes.length > 0 && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Änderungstypen
          </Typography>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              bgcolor: statSurfaceBg,
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: '400px',
                }}
              >
                <thead>
                  <tr>
                    <th style={htmlThStyle()}>Änderungstyp</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Anzahl</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Anteil</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.changeTypes.map(type => (
                    <tr key={type.key}>
                      <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                        {type.label}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {type.count}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {stats.counts.totalCCChanges > 0
                          ? percentFormatter.format((type.count / stats.counts.totalCCChanges) * 100)
                          : 0}{' '}
                        %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Paper>
        </section>
      )}
    </StatisticViewShell>
    </div>
  );
};

export default CallCenterStatistics;
